import React, { useState } from 'react';
import { Save, Trash2, Plus, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';

const NotePad = () => {
  const [notes, setNotes] = useState([
    {
      id: '1',
      title: 'AI Prompt Ideas',
      content: 'List of creative prompts to try:\n- Portrait photography styles\n- Abstract art concepts\n- Story writing prompts',
      updatedAt: '2024-01-15',
    },
  ]);
  const [activeNote, setActiveNote] = useState(notes[0] || null);
  const [title, setTitle] = useState(activeNote?.title || '');
  const [content, setContent] = useState(activeNote?.content || '');

  const handleNewNote = () => {
    const newNote = {
      id: Date.now().toString(),
      title: 'Untitled Note',
      content: '',
      updatedAt: new Date().toISOString().split('T')[0],
    };
    setNotes([newNote, ...notes]);
    setActiveNote(newNote);
    setTitle(newNote.title);
    setContent(newNote.content);
  };

  const handleSave = () => {
    if (!activeNote) return;
    const updatedNotes = notes.map((note) =>
      note.id === activeNote.id
        ? { ...note, title, content, updatedAt: new Date().toISOString().split('T')[0] }
        : note
    );
    setNotes(updatedNotes);
  };

  const handleDelete = (id) => {
    const filtered = notes.filter((note) => note.id !== id);
    setNotes(filtered);
    if (activeNote?.id === id) {
      setActiveNote(filtered[0] || null);
      setTitle(filtered[0]?.title || '');
      setContent(filtered[0]?.content || '');
    }
  };

  const handleSelectNote = (note) => {
    setActiveNote(note);
    setTitle(note.title);
    setContent(note.content);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
            <FileText size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">NotePad</h1>
            <p className="text-gray-400">Save your ideas and prompts</p>
          </div>
        </div>
        <Button onClick={handleNewNote} className="bg-purple-600 hover:bg-purple-700">
          <Plus size={18} className="mr-2" />
          New Note
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Notes List */}
        <div className="bg-[#1a1a2e] rounded-xl p-4 border border-gray-800 h-[500px] overflow-y-auto">
          <h2 className="text-white font-semibold mb-4">Your Notes</h2>
          <div className="space-y-2">
            {notes.map((note) => (
              <div
                key={note.id}
                onClick={() => handleSelectNote(note)}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  activeNote?.id === note.id
                    ? 'bg-purple-600/30 border border-purple-500'
                    : 'bg-[#12121a] border border-gray-800 hover:border-gray-700'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{note.title}</h3>
                    <p className="text-gray-500 text-xs mt-1">{note.updatedAt}</p>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(note.id);
                    }}
                    className="text-gray-500 hover:text-red-500 h-8 w-8"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
            {notes.length === 0 && (
              <p className="text-gray-500 text-center py-8">No notes yet</p>
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-2 bg-[#1a1a2e] rounded-xl p-4 border border-gray-800">
          {activeNote ? (
            <div className="space-y-4 h-[468px] flex flex-col">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Note title"
                className="bg-[#12121a] border-gray-700 text-white text-lg font-semibold"
              />
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Start typing..."
                className="flex-1 bg-[#12121a] border-gray-700 text-white resize-none"
              />
              <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                <Save size={18} className="mr-2" />
                Save Note
              </Button>
            </div>
          ) : (
            <div className="h-[468px] flex flex-col items-center justify-center">
              <FileText size={48} className="text-gray-600 mb-4" />
              <p className="text-gray-400">Select a note or create a new one</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotePad;
